import { defineStore } from 'pinia';
import { ref } from 'vue';
import  * as CrewService  from '@/services/crew.service';
import type { CrewMember } from '@/models/CrewMember';
import { useAuth0 } from "@auth0/auth0-vue";
import * as DateUtils from "@/helpers/dateUtils";
import {useI18n} from "vue-i18n";
const crewLists = ref<any[]>([]);


export const useCrewStore = defineStore('crew', () => {
    const crewList = ref<CrewMember[]>([]);
    const crewListName = ref('');
    const status = ref<'idle' | 'loading' | 'success' | 'error'>('idle');
    const selectedCrewMember = ref<CrewMember | null>(null);
    const listUuid = ref<string | null>(null);

    const {user: auth0User} = useAuth0();

    const i18n = useI18n({ useScope: "global" });


    function fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () =>
                resolve(reader.result?.toString().split(",")[1] || "");
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    async function saveCrewMember(listId: string, payload: Partial<CrewMember>) {
        try {
            const { data } = await CrewService.createCrewMember(listId, payload);
            return data;
        } catch (error) {
            console.error('Failed to create crew member:', error);
            throw error;
        }
    }

    async function createCrewList(listName: string) {
        try {
            const { data } = await CrewService.createCrewList({ listName });
            crewLists.value.push(data);
            return data;
        } catch (err) {
            console.error("Failed to create crew list:", err);
            throw err;
        }
    }

    async function fetchAllCrewLists() {
        status.value = 'loading';
        try {
            const { data } = await CrewService.getCrewList();
            console.log(data);
            crewList.value = data;
            if (data.length > 0) {
                if (data[0].listName) {
                    crewListName.value = data[0].listName;
                }
                listUuid.value = data[0].uuid;
            }
            status.value = 'success';
        } catch (error) {
            console.error('Failed to fetch all crew lists:', error);
            status.value = 'error';
        }
    }

    async function fetchCrewList(listId?: string) {
        status.value = 'loading';
        try {
            let data;
            if (listId) {
                ({ data } = await CrewService.getCrewListById(listId));
            } else {
                ({ data } = await CrewService.getCrewList());
            }

            crewList.value = data;

            if (data.length > 0) {
                if (data[0].listName) {
                    crewListName.value = data[0].listName;
                }
                listUuid.value = data[0].uuid;
            }

            status.value = 'success';
        } catch (error) {
            console.error('Failed to fetch crew list:', error);
            status.value = 'error';
        }
    }



    async function deleteCrewList(listId: string) {
        try {
            await CrewService.deleteCrewListById(listId);
            crewList.value = [];
            crewListName.value = '';
        } catch (error) {
            console.error('Failed to delete crew list:', error);
            throw error;
        }
    }

    async function updateCrewList(listId: string, newName: string) {
        try {
            const payload = { name: newName };
            await CrewService.updateCrewListById(listId, payload);
            crewListName.value = newName;
        } catch (error) {
            console.error('Failed to update crew list:', error);
            throw error;
        }
    }


    async function fetchCrewMember(listId: string, crewId: string) {
        try {
            const { data } = await CrewService.getCrewMember(listId, crewId);
            selectedCrewMember.value = {
                ...data,
                profileImage: data.profileImage || auth0User.value.picture,
                name: data.name || auth0User.value.name || "",
                birthDate: data.birthDate ? DateUtils.fromApiDate(data.birthDate, i18n.locale.value) : null,
                passportExpiryDate: data.passportExpiryDate ? DateUtils.fromApiDate(data.passportExpiryDate, i18n.locale.value) : null,
            };
        } catch (error) {
            console.error('Failed to fetch crew member:', error);
        }
    }

    async function updateCrewMember(listId: string, crewId: string, payload: Partial<CrewMember>) {
        try {
            if (payload.birthDate) {
                payload.birthDate = DateUtils.toApiDate(payload.birthDate, i18n.locale.value);
            }
            if (payload.passportExpiryDate) {
                payload.passportExpiryDate = DateUtils.toApiDate(payload.passportExpiryDate, i18n.locale.value);
            }
            const response = await CrewService.updateCrewMember(listId, crewId, payload);
            const updated = response.data;
            
            return updated;
        } catch (error) {
            console.error('Failed to update crew member:', error);
            throw error;
        }
    }
    
    async function removeCrewMember(listId: string, crewId: string) {
        try {
            await CrewService.deleteCrewMember(listId, crewId);

        } catch (error) {
            console.error('Failed to delete crew member:', error);
            throw error;
        }
    }
    
    async function uploadPassportDocument(listId: string, crewId: string, file: File) {
        console.log('Uploading passport document for member:', listId, crewId, file);
        
        try {
            
            if (!(file instanceof File)) {
                console.error("Invalid file object:", file);
                throw new Error("Provided input is not a valid File.");
            }

            const base64Data = await fileToBase64(file);

            const payload = {
                file: base64Data,
                filename: file.name,
                type: file.type,
                size: file.size,
            };

            console.log('Calling upload for crew:', listId, crewId, file);
            console.log('Is file a File?', file instanceof File);

            const response = await CrewService.updatePassportDocuments(listId, crewId, payload);

            if (selectedCrewMember.value) {
                selectedCrewMember.value.passportDocuments = response.data.passportDocuments;
            }
        } catch (error) {
            console.error("Passport upload failed:", error);
            throw error;
        }
    }
    
    async function getPassportDocuments(listId: string, crewId: string) {
        try {
            const response = await CrewService.getPassportDocuments(listId, crewId);
            if (selectedCrewMember.value && selectedCrewMember.value.uuid === crewId) {
                selectedCrewMember.value.passportDocuments = response.data.passportDocuments ?? response.data;
            }
            return response.data;
        } catch (error) {
            console.error("Fetching passport documents failed:", error);
            throw error;
        }
    }

    async function deletePassportDocument(listId: string, crewId: string, uuid: string) {
        try {
            await CrewService.deletePassportDocument(listId, crewId, uuid);
            if (selectedCrewMember.value && selectedCrewMember.value.passportDocuments) {
                selectedCrewMember.value.passportDocuments = selectedCrewMember.value.passportDocuments.filter((doc: any) => doc.uuid !== uuid);
            }
        } catch (error) {
            console.error("Passport document deletion failed:", error);
            throw error;
        }
    }

    async function uploadLicenseDocument(listId: string, crewId: string, file: File) {
        console.log('Uploading license document for member:', listId, crewId, file);
        try {
            if (!(file instanceof File)) {
                console.error("Invalid file object:", file);
                throw new Error("Provided input is not a valid File.");
            }

            const base64Data = await fileToBase64(file);
            const payload = {
                file: base64Data,
                filename: file.name,
                type: file.type,
                size: file.size,
            };

            const response = await CrewService.updateLicenseDocuments(listId, crewId, payload);

            if (selectedCrewMember.value) {
                selectedCrewMember.value.licenseDocuments = response.data.licenseDocuments ?? response.data;
            }
        } catch (error) {
            console.error("License upload failed:", error);
            throw error;
        }
    }

    async function getLicenseDocuments(listId: string, crewId: string) {
        try {
            const response = await CrewService.getLicenseDocuments(listId, crewId);
            if (selectedCrewMember.value && selectedCrewMember.value.uuid === crewId) {
                selectedCrewMember.value.licenseDocuments = response.data.licenseDocuments ?? response.data;
            }
            return response.data;
        } catch (error) {
            console.error("Fetching license documents failed:", error);
            throw error;
        }
    }

    async function deleteLicenseDocument(listId: string, crewId: string, uuid: string) {
        try {
            await CrewService.deleteLicenseDocument(listId, crewId, uuid);
            if (selectedCrewMember.value && selectedCrewMember.value.licenseDocuments) {
                selectedCrewMember.value.licenseDocuments = selectedCrewMember.value.licenseDocuments.filter((doc: any) => doc.uuid !== uuid);
            }
        } catch (error) {
            console.error("License document deletion failed:", error);
            throw error;
        }
    }

    async function uploadVHFLicenseDocument(listId: string, crewId: string, file: File) {
        console.log('Uploading VHF license document for member:', listId, crewId, file);
        try {
            if (!(file instanceof File)) {
                console.error("Invalid file object:", file);
                throw new Error("Provided input is not a valid File.");
            }

            const base64Data = await fileToBase64(file);
            const payload = {
                file: base64Data,
                filename: file.name,
                type: file.type,
                size: file.size,
            };

            const response = await CrewService.updateVhfDocuments(listId, crewId, payload);

            if (selectedCrewMember.value) {
                selectedCrewMember.value.vhfLicenseDocuments = response.data.vhfLicenseDocuments ?? response.data;
            }
        } catch (error) {
            console.error("VHF License upload failed:", error);
            throw error;
        }
    }

    async function getVHFLicenseDocuments(listId: string, crewId: string) {
        try {
            const response = await CrewService.getVhfDocuments(listId, crewId);
            if (selectedCrewMember.value && selectedCrewMember.value.uuid === crewId) {
                selectedCrewMember.value.vhfLicenseDocuments = response.data.vhfLicenseDocuments ?? response.data;
            }
            return response.data;
        } catch (error) {
            console.error("Fetching VHF license documents failed:", error);
            throw error;
        }
    }

    async function deleteVHFLicenseDocument(listId: string, crewId: string, uuid: string) {
        try {
            await CrewService.deleteVhfDocument(listId, crewId, uuid);
            if (selectedCrewMember.value && selectedCrewMember.value.vhfLicenseDocuments) {
                selectedCrewMember.value.vhfLicenseDocuments = selectedCrewMember.value.vhfLicenseDocuments.filter((doc: any) => doc.uuid !== uuid);
            }
        } catch (error) {
            console.error("VHF license document deletion failed:", error);
            throw error;
        }
    }
    

    return {
        crewList,
        crewListName,
        status,
        listUuid,
        selectedCrewMember,

        fetchAllCrewLists,
        fetchCrewList,
        createCrewList,
        updateCrewMember,
        uploadPassportDocument,
        fetchCrewMember,
        saveCrewMember,
        removeCrewMember,
        deleteCrewList,
        updateCrewList,

        getPassportDocuments,
        deletePassportDocument,

        uploadLicenseDocument,
        getLicenseDocuments,
        deleteLicenseDocument,

        uploadVHFLicenseDocument,
        getVHFLicenseDocuments,
        deleteVHFLicenseDocument,
    };
});